import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError } from '@crave/api-client';
import { Button, Card, Field, FormGrid, SelectField } from '@crave/ui';
import { api, orderSiteUrl } from '../api.ts';
import { ConfirmDelete } from '../components/ConfirmDelete.tsx';
import { HistoryLink } from '../components/HistoryLink.tsx';
import { QrCode } from '../components/QrCode.tsx';
import { Notice, RowActions, Section, Table, errorText, money, when } from '../components/ui.tsx';
import { useStaff } from '../lib/staff.tsx';
import {
  CantDelete,
  tastingConsequences,
  withReason,
  type TastingsLocationState,
} from './Tastings.tsx';

interface Batch {
  id: string;
  batch_code: string;
  item_name: string;
  pizzas: number;
  slices_per_pizza: number;
  baked_at: string;
  inventory_posted: boolean;
  samples: number;
  responses: number;
  avg_overall: number | null;
  cost_cents: number;
  can_delete?: boolean;
  delete_blocked_reason?: string | null;
}

interface Detail {
  campaign: { id: string; name: string; isActive: boolean; notes: string | null };
  can_delete?: boolean;
  delete_blocked_reason?: string | null;
  batches: Batch[];
  samples: {
    id: string;
    batch_id: string;
    sample_code: string;
    label: string | null;
    overall: number | null;
    would_order: string | null;
    comment: string | null;
    contact_email: string | null;
  }[];
  /** Every response about this campaign: printed slips, the table QR and what staff logged. */
  reviews?: {
    id: string;
    overall: number | null;
    would_order: string | null;
    comment: string | null;
    first_name: string | null;
    contact_email: string | null;
    created_at: string;
    sample_code: string | null;
    logged_by: string | null;
    batch_code: string | null;
  }[];
}

/** A checkbox and its text, centred on one line with a touch-sized row. */
const checkRow: CSSProperties = {
  display: 'flex',
  gap: 10,
  alignItems: 'center',
  minHeight: 44,
  cursor: 'pointer',
};
const checkBox: CSSProperties = { width: 18, height: 18, margin: 0, flex: 'none' };

const againLabel = (v: string | null) =>
  v === 'YES' ? 'Yes' : v === 'MAYBE' ? 'Maybe' : v === 'NO' ? 'No' : '';

export function TastingDetail() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { can } = useStaff();
  const canDelete = can('records.delete');
  const q = useQuery({
    queryKey: ['tasting', id],
    queryFn: async () => (await api.get<Detail>(`/admin/tastings/${id}`)).data,
  });
  const items = useQuery({
    queryKey: ['admin-menu'],
    queryFn: async () =>
      (await api.get<{ items: { id: string; name: string }[] }>('/admin/menu/items')).data.items,
  });
  const locs = useQuery({
    queryKey: ['admin-locations'],
    queryFn: async () =>
      (await api.get<{ items: { id: string; name: string }[] }>('/admin/locations')).data.items,
  });
  const [form, setForm] = useState({ item: '', pizzas: '1', slices: '8', post: true });
  useEffect(() => {
    const first = items.data?.[0];
    if (first && !form.item) setForm((f) => ({ ...f, item: first.id }));
  }, [items.data, form.item]);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [printBatch, setPrintBatch] = useState<string | null>(null);
  /** The one-page card that stands on the table, with the QR for the whole batch. */
  const [printCard, setPrintCard] = useState<Batch | null>(null);
  const [deleting, setDeleting] = useState<'campaign' | Batch | null>(null);
  const bake = useMutation({
    mutationFn: () =>
      api.post<{ batch_code: string }>(`/admin/tastings/${id}/batches`, {
        location_id: locs.data?.[0]?.id,
        menu_item_id: form.item,
        pizzas: Number(form.pizzas),
        slices_per_pizza: Number(form.slices),
        post_inventory: form.post,
      }),
    onSuccess: (r) => {
      setMsg({
        kind: 'ok',
        text: `Batch ${r.data.batch_code} recorded. Print its QR codes below.`,
      });
      void qc.invalidateQueries({ queryKey: ['tasting', id] });
    },
    onError: (e) =>
      setMsg({ kind: 'error', text: e instanceof ApiError ? e.message : 'Could not record.' }),
  });
  const toggle = useMutation({
    mutationFn: (active: boolean) => api.patch(`/admin/tastings/${id}`, { is_active: active }),
    onSuccess: (_r, active) => {
      setMsg({
        kind: 'ok',
        text: active
          ? 'Feedback reopened. Guests can rate samples again.'
          : 'Feedback closed. Guests can no longer rate samples.',
      });
      void qc.invalidateQueries({ queryKey: ['tasting', id] });
    },
    onError: (e, active) =>
      setMsg({
        kind: 'error',
        text: `${active ? 'Could not reopen feedback.' : 'Could not close feedback.'} ${errorText(e, 'Try again.')}`,
      }),
  });

  if (q.isError) return <Notice kind="error">Could not load this tasting.</Notice>;
  if (!q.data) return <p>Loading…</p>;
  const d = q.data;
  const printing = printBatch ? d.samples.filter((s) => s.batch_id === printBatch) : [];
  const campaignBlocked = d.can_delete === false;
  const totals = {
    batches: d.batches.length,
    samples: d.batches.reduce((n, b) => n + b.samples, 0),
    responses: d.batches.reduce((n, b) => n + b.responses, 0),
  };
  const refreshLists = () => {
    void qc.invalidateQueries({ queryKey: ['tastings'] });
    void qc.invalidateQueries({ queryKey: ['feedback'] });
  };

  let dialog: ReactNode = null;
  if (deleting === 'campaign') {
    dialog = (
      <ConfirmDelete
        open
        onClose={() => setDeleting(null)}
        title={`Delete ${d.campaign.name}?`}
        description={
          totals.batches > 0
            ? 'Anything these batches took out of inventory stays recorded in inventory.'
            : 'This tasting has no batches yet.'
        }
        consequences={tastingConsequences(totals)}
        askReason
        onConfirm={async ({ reason }) => {
          await api.delete(withReason(`/admin/tastings/${id}`, reason));
        }}
        onDone={() => {
          refreshLists();
          const state: TastingsLocationState = { deleted: d.campaign.name };
          void navigate('/tastings', { replace: true, state });
          // Drop the cached page only after leaving it, so it does not flash "Loading…".
          setTimeout(() => qc.removeQueries({ queryKey: ['tasting', id] }), 0);
        }}
        alternative={
          d.campaign.isActive
            ? {
                label: 'Close feedback',
                description:
                  'You can close feedback instead. The tasting and its results stay, and guests can no longer rate samples.',
                pendingLabel: 'Closing…',
                onClick: async () => {
                  await toggle.mutateAsync(false);
                },
              }
            : undefined
        }
      />
    );
  } else if (deleting) {
    const b = deleting;
    dialog = (
      <ConfirmDelete
        open
        onClose={() => setDeleting(null)}
        title={`Delete batch ${b.batch_code}?`}
        description={`${b.item_name}, baked ${when(b.baked_at)}.${
          b.inventory_posted
            ? ' Anything it took out of inventory stays recorded in inventory.'
            : ''
        }`}
        consequences={tastingConsequences(b)}
        askReason
        onConfirm={async ({ reason }) => {
          await api.delete(withReason(`/admin/tastings/batches/${b.id}`, reason));
          setMsg({ kind: 'ok', text: `Deleted batch ${b.batch_code}.` });
          if (printBatch === b.id) setPrintBatch(null);
          if (printCard?.id === b.id) setPrintCard(null);
          void qc.invalidateQueries({ queryKey: ['tasting', id] });
          refreshLists();
        }}
      />
    );
  }

  return (
    <Section
      title={d.campaign.name}
      right={
        <div
          style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}
          className="no-print"
        >
          <Button
            variant="secondary"
            onClick={() => toggle.mutate(!d.campaign.isActive)}
            disabled={toggle.isPending}
          >
            {d.campaign.isActive ? 'Close feedback' : 'Reopen feedback'}
          </Button>
          {canDelete && !campaignBlocked && (
            <Button
              variant="danger"
              onClick={() => {
                setMsg(null);
                setDeleting('campaign');
              }}
            >
              Delete tasting
            </Button>
          )}
          <HistoryLink entityType="tasting_campaigns" entityId={id} name={d.campaign.name} />
          <Link to="/tastings" className="crave-back-link">
            All tastings
          </Link>
        </div>
      }
    >
      <style>{`@media print { nav, .no-print { display: none !important; } main { padding: 0 !important; } .qr-sheet { display: grid !important; } }`}</style>
      <div className="no-print" style={{ display: 'grid', gap: 12 }}>
        {canDelete && campaignBlocked && (
          <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-soft)' }}>
            This tasting took ingredients out of inventory, so it can’t be deleted. Close feedback
            to stop new ratings instead.
          </p>
        )}
        {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
        {(items.isError || locs.isError) && (
          <Notice kind="error">
            Could not load pizzas or your location, so batches can't be recorded.{' '}
            {errorText(items.error ?? locs.error, '')}
          </Notice>
        )}
        <Card>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              bake.mutate();
            }}
            style={{ display: 'grid', gap: 0 }}
          >
            <FormGrid min={200} style={{ maxWidth: 760 }}>
              <SelectField
                label="Pizza"
                value={form.item}
                onChange={(e) => setForm({ ...form, item: e.target.value })}
                options={(items.data ?? []).map((m) => [m.id, m.name] as const)}
              />
              <Field
                label="Pizzas baked"
                inputMode="numeric"
                value={form.pizzas}
                onChange={(e) => setForm({ ...form, pizzas: e.target.value })}
              />
              <Field
                label="Slices per pizza"
                inputMode="numeric"
                value={form.slices}
                onChange={(e) => setForm({ ...form, slices: e.target.value })}
              />
            </FormGrid>
            <label style={checkRow}>
              <input
                type="checkbox"
                checked={form.post}
                onChange={(e) => setForm({ ...form, post: e.target.checked })}
                style={checkBox}
              />
              Take ingredients out of inventory
            </label>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
              <Button type="submit" disabled={bake.isPending || !form.item || !locs.data?.length}>
                Record batch
              </Button>
            </div>
          </form>
        </Card>
        <Table
          head={[
            'Batch',
            'Pizza',
            'Baked',
            'Slices',
            'Responses',
            'Avg',
            'Ingredient cost',
            canDelete ? '' : 'QR codes',
          ]}
          stickyActions
          rowKeys={d.batches.map((b) => b.id)}
          rows={d.batches.map((b) => [
            b.batch_code,
            b.item_name,
            when(b.baked_at),
            String(b.samples),
            String(b.responses),
            b.avg_overall === null ? 'No ratings' : `${b.avg_overall.toFixed(1)}/5`,
            b.inventory_posted ? money(b.cost_cents) : 'Not taken from stock',
            <RowActions>
              <Button
                variant="secondary"
                aria-label={`Print the table card for batch ${b.batch_code}`}
                onClick={() => {
                  setPrintBatch(null);
                  setPrintCard(b);
                  setTimeout(() => window.print(), 400);
                }}
              >
                Table card
              </Button>
              <Button
                variant="secondary"
                aria-label={`Print QR codes for batch ${b.batch_code}`}
                onClick={() => {
                  setPrintCard(null);
                  setPrintBatch(b.id);
                  setTimeout(() => window.print(), 300);
                }}
              >
                Print slips
              </Button>
              {canDelete &&
                (b.can_delete === false ? (
                  <CantDelete>Can’t delete, it used stock</CantDelete>
                ) : (
                  <Button
                    variant="danger"
                    aria-label={`Delete batch ${b.batch_code}`}
                    onClick={() => {
                      setMsg(null);
                      setDeleting(b);
                    }}
                  >
                    Delete
                  </Button>
                ))}
            </RowActions>,
          ])}
          empty="No batches yet."
        />
        <h3 style={{ margin: 0 }}>Responses</h3>
        <Table
          head={['When', 'Pizza', 'From', 'Overall', 'Again?', 'Comment', 'Email']}
          wrapColumns={[5]}
          rows={(d.reviews ?? []).map((f) => [
            when(f.created_at),
            f.batch_code ?? '',
            f.sample_code
              ? `Slip ${f.sample_code}`
              : f.logged_by
                ? `${f.first_name ? `${f.first_name} · ` : ''}Logged by ${f.logged_by}`
                : `${f.first_name ? `${f.first_name} · ` : ''}Table QR`,
            f.overall === null ? '' : `${f.overall}/5`,
            againLabel(f.would_order),
            f.comment ?? '',
            f.contact_email ?? '',
          ])}
          empty="No responses yet."
        />
      </div>
      {printCard && (
        <div
          className="qr-sheet"
          style={{
            marginTop: 16,
            border: '1px solid var(--line)',
            borderRadius: 16,
            padding: 32,
            textAlign: 'center',
            display: 'grid',
            gap: 12,
            justifyItems: 'center',
            breakInside: 'avoid',
          }}
        >
          <div style={{ fontSize: 15, letterSpacing: '.14em', textTransform: 'uppercase' }}>
            Crave Pizza · tasting
          </div>
          <div style={{ fontSize: 34, fontWeight: 800, lineHeight: 1.1 }}>
            How was the {printCard.item_name}?
          </div>
          <QrCode value={`${orderSiteUrl}/taste/${printCard.batch_code}`} size={260} />
          <div style={{ fontSize: 18 }}>
            Point your camera at this. Twenty seconds, no account, and it decides what we put on the
            menu.
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
            order.cravepizzafl.com/taste/{printCard.batch_code}
          </div>
        </div>
      )}
      {printing.length > 0 && (
        <div
          className="qr-sheet"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginTop: 16 }}
        >
          {printing.map((s) => (
            <div
              key={s.id}
              style={{
                border: '1px dashed var(--line)',
                borderRadius: 8,
                padding: 10,
                textAlign: 'center',
                breakInside: 'avoid',
              }}
            >
              <QrCode value={`${orderSiteUrl}/taste/${s.sample_code}`} size={120} />
              <div style={{ fontWeight: 800, letterSpacing: '.08em' }}>{s.sample_code}</div>
              <div style={{ fontSize: 11 }}>Scan to rate this slice</div>
            </div>
          ))}
        </div>
      )}
      {dialog}
    </Section>
  );
}
